import React, {
  forwardRef,
  useEffect,
  useCallback,
  useState,
  useRef,
  useImperativeHandle,
} from 'react';

import { findDOMNode } from 'react-dom';
import PropTypes from 'prop-types';
import ReactTooltip from 'react-tooltip';
import uuidv4 from 'uuid/v4';
import classnames from 'classnames';

import getClosestScrollParent from 'utils/get-closest-scroll-parent';

import optionWithToolTipHoc from './option-with-tooltip';
import SelectMenuStandard from './select-menu-standard';

/**
 * The decorator function for use with decorateWithDefaultProps to decorate the `react-select` components
 * with default props and to add additional props of `primary` to support contextual design switching
 * on the ComposedComponent, and tooltip for enabling tooltip decorated Option components in the Select.
 *
 * @param {Function} ComposedComponent - the JSX component or stateless function to decorate
 * @param {Object} defaultProps - the defaultProps curried from the decorateWithDefaultProps function call.
 * @returns {Function} DecoratedComponent - the decorated component with the defaultProps applied.
 **/
const selectComponentDecorator = (ComposedComponent, defaultProps = {}) => {
  const tooltipId = uuidv4();

  const combineProps = props => {
    const className = classnames(
      props.className,
      defaultProps.className,
      { [defaultProps.fullWidthClassName]: props.flex },
      { [defaultProps.isPrimaryClassName]: props.primary },
      { [defaultProps.isMenuDetachedClassName]: props.detachMenu },
      { [defaultProps.sizeClassName[`${props.size}`]]: props.size },
      { [defaultProps.themeClassName[`${props.theme}`]]: props.theme },
      {
        [defaultProps.alignMenuClassName[`${props.alignMenu}`]]:
          props.detachMenu && props.alignMenu,
      }
    );

    return {
      ...props,
      ...defaultProps,
      className,
      ...(props.tooltip
        ? {
            optionComponent: optionWithToolTipHoc(
              tooltipId,
              props.optionComponent
            ),
          }
        : {}),
    };
  };

  // eslint-disable-next-line react/no-multi-comp
  const DecoratedSelect = forwardRef((props, ref) => {
    const combinedProps = combineProps(props);

    const {
      autoMenuPlacement,
      menuIntersectionRoot: initMenuIntersectionRoot,
      menuLink: menuLink,
      menuTitle: menuTitle,
      menuPlacement: initMenuPlacement,
      menuRenderer: MenuRenderer,
      detachMenu,
      tooltipOptions,
      className,
      ...otherProps
    } = combinedProps;

    // Refs
    const selectRef = useRef(null);

    // State
    const [menuIntersectionRoot, setMenuIntersectionRoot] = useState(
      initMenuIntersectionRoot
    );
    const [menuPlacement, setMenuPlacement] = useState(initMenuPlacement);

    // Effects
    useImperativeHandle(ref, () => selectRef.current);

    useEffect(() => {
      // If a menuIntersectionRoot is not provided, attempt to find the closest scroll parent
      if (initMenuIntersectionRoot === null) {
        const node = findDOMNode(selectRef.current);
        const scrollParent = getClosestScrollParent(node);

        setMenuIntersectionRoot(scrollParent);
      }
    }, [selectRef]);

    // Callbacks
    const onMenuPlacementChange = useCallback(
      placement => {
        if (autoMenuPlacement) {
          setMenuPlacement(placement);
        }
      },
      [autoMenuPlacement]
    );

    // Wrap the menuRenderer passed in via props with a callback function
    // So that we can decorate it with our autoPlacement props
    const menuRendererFnc = useCallback(
      menuRendererProps => (
        <MenuRenderer
          {...menuRendererProps}
          menuLink={menuLink}
          menuTitle={menuTitle}
          detachMenu={detachMenu}
          autoMenuPlacement={autoMenuPlacement}
          placement={menuPlacement}
          intersectionRoot={menuIntersectionRoot}
          onMenuPlacementChange={onMenuPlacementChange}
        />
      ),
      [
        autoMenuPlacement,
        detachMenu,
        menuIntersectionRoot,
        menuLink,
        menuPlacement,
        menuTitle,
        MenuRenderer,
        onMenuPlacementChange,
      ]
    );

    const _className = classnames(className, {
      [defaultProps.menuPlacementClassName[
        `${autoMenuPlacement ? menuPlacement : initMenuPlacement}`
      ]]: true,
    });

    return otherProps.tooltip ? (
      <div ref={selectRef} className={defaultProps.className}>
        <ComposedComponent
          {...otherProps}
          className={_className}
          menuRenderer={menuRendererFnc}
        />
        <ReactTooltip {...tooltipOptions} id={tooltipId} />
      </div>
    ) : (
      <ComposedComponent
        {...otherProps}
        ref={selectRef}
        className={_className}
        menuRenderer={menuRendererFnc}
      />
    );
  });

  if (ComposedComponent.name) {
    DecoratedSelect.displayName = `Charts${ComposedComponent.name}`;
  }

  if (ComposedComponent.displayName) {
    DecoratedSelect.displayName = `Charts${ComposedComponent.displayName}`;
  }

  DecoratedSelect.propTypes = {
    ...ComposedComponent.propTypes,
    autoMenuPlacement: PropTypes.bool,
    alignMenu: PropTypes.oneOf(['left', 'right']), // Can only be used with detachMenu
    detachMenu: PropTypes.bool,
    menuIntersectionRoot: PropTypes.any,
    menuLink: PropTypes.shape({
      // Can only be used with the SelectMenuWithHeader menuRenderer
      text: PropTypes.string,
      href: PropTypes.string,
      target: PropTypes.target,
    }),
    menuPlacement: PropTypes.oneOf(['top', 'bottom']),
    menuTitle: PropTypes.string, // Can only be used with the SelectMenuWithHeader menuRenderer
    primary: PropTypes.bool,
    flex: PropTypes.bool,
    size: PropTypes.oneOf(['xs', 'sm', 'm', 'lg', 'xl']),
    theme: PropTypes.oneOf(['raised', 'flat', 'empty']),
    tooltip: PropTypes.bool,
    tooltipOptions: PropTypes.object,
  };

  DecoratedSelect.defaultProps = {
    ...ComposedComponent.defaultProps,
    autoMenuPlacement: true,
    alignMenu: 'left',
    detachMenu: false,
    menuIntersectionRoot: null,
    menuLink: {},
    menuPlacement: 'bottom',
    menuRenderer: SelectMenuStandard,
    menuTitle: '',
    primary: false,
    flex: true,
    size: 'm',
    theme: 'raised',
    tooltip: false,
    tooltipOptions: {
      ...ReactTooltip.defaultProps,
    },
  };

  return DecoratedSelect;
};

export default selectComponentDecorator;
export { selectComponentDecorator };
